/**
 * Repo-private landing parameters must never reach analytics or advertising tags.
 *
 * The root layout runs `PRIVATE_URL_PARAM_STAGING_SCRIPT` as a `beforeInteractive`
 * script, which Next.js executes before hydration and therefore before GTM, the
 * Meta Pixel, PostHog, and the App Router read the URL. It copies each raw value
 * into page memory and optionally session storage, then removes it from the URL.
 * Attribution capture then consumes the staged values once. `proxy.ts` also
 * renders page requests without these parameters, so server markup never echoes
 * them into links or return paths.
 *
 * Storage failures never prevent URL removal. Page memory keeps the handoff
 * available for capture even when storage access throws or writes are ignored.
 *
 * OpenAI Ads `oppref` is deliberately not staged: the OpenAI Measurement Pixel in
 * GTM reads it from the landing URL to set its own `__oppref` cookie (see
 * docs/GTM_CONFIGURATION_GUIDE.md). PostHog payloads drop it in
 * `posthog-exception-filter.ts` instead.
 */

export const STAGED_PRIVATE_URL_PARAMS = ["ct_attr"] as const;
export type StagedPrivateUrlParam = (typeof STAGED_PRIVATE_URL_PARAMS)[number];
export type StagedPrivateUrlParams = Partial<Record<StagedPrivateUrlParam, string>>;

export const PRIVATE_URL_PARAM_STAGE_KEY = "chicky_private_url_params_v1";
declare global {
  interface Window {
    [PRIVATE_URL_PARAM_STAGE_KEY]?: string;
  }
}
export const PRIVATE_URL_PARAM_MAX_AGE_MS = 30 * 60 * 1000;
const STAGED_VALUE_MAX_CHARS = 2048;

// Plain ES5 so it runs unchanged as an inline script on every supported browser.
export const PRIVATE_URL_PARAM_STAGING_SCRIPT = `(function () {
  try {
    var search = window.location.search;
    if (!search || search.length < 2) return;
    var names = ${JSON.stringify(STAGED_PRIVATE_URL_PARAMS)};
    var parts = search.slice(1).split("&");
    var kept = [];
    var staged = {};
    var found = false;
    var hasValue = false;
    for (var i = 0; i < parts.length; i++) {
      var part = parts[i];
      if (!part) continue;
      var separator = part.indexOf("=");
      var name = separator === -1 ? part : part.slice(0, separator);
      if (names.indexOf(name) === -1) {
        kept.push(part);
        continue;
      }
      found = true;
      var value = separator === -1 ? "" : part.slice(separator + 1);
      if (value && !Object.prototype.hasOwnProperty.call(staged, name)) {
        staged[name] = value;
        hasValue = true;
      }
    }
    if (!found) return;
    if (hasValue) {
      var record = JSON.stringify({ values: staged, at: Date.now() });
      window[${JSON.stringify(PRIVATE_URL_PARAM_STAGE_KEY)}] = record;
      try {
        window.sessionStorage.setItem(${JSON.stringify(PRIVATE_URL_PARAM_STAGE_KEY)}, record);
      } catch (error) {}
    }
    var nextUrl =
      window.location.pathname + (kept.length ? "?" + kept.join("&") : "") + window.location.hash;
    window.history.replaceState(window.history.state, "", nextUrl);
  } catch (error) {}
})();`;

/**
 * Return and remove the values staged for this page load. Values are raw query
 * substrings: never decode, trim, or normalize them.
 */
export function consumeStagedPrivateUrlParams(
  storage?: Storage,
  now = Date.now()
): StagedPrivateUrlParams {
  let stored: string | null = null;
  if (typeof window !== "undefined") {
    stored = window[PRIVATE_URL_PARAM_STAGE_KEY] ?? null;
    delete window[PRIVATE_URL_PARAM_STAGE_KEY];
  }
  try {
    const browserStorage =
      storage ?? (typeof window !== "undefined" ? window.sessionStorage : undefined);
    const persisted = browserStorage?.getItem(PRIVATE_URL_PARAM_STAGE_KEY);
    if (persisted) browserStorage?.removeItem(PRIVATE_URL_PARAM_STAGE_KEY);
    stored ||= persisted ?? null;
  } catch {
    // Keep the page-memory copy if storage access or removal is blocked.
  }
  if (!stored) return {};

  try {
    const parsed = JSON.parse(stored) as { values?: unknown; at?: unknown };
    const age = now - Number(parsed.at);
    if (!Number.isFinite(age) || age < 0 || age > PRIVATE_URL_PARAM_MAX_AGE_MS) return {};
    if (!parsed.values || typeof parsed.values !== "object") return {};

    const values = parsed.values as Record<string, unknown>;
    const result: StagedPrivateUrlParams = {};
    for (const name of STAGED_PRIVATE_URL_PARAMS) {
      const value = values[name];
      if (typeof value === "string" && value && value.length <= STAGED_VALUE_MAX_CHARS) {
        result[name] = value;
      }
    }
    return result;
  } catch {
    return {};
  }
}
